import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { api, unwrap } from "../api";
import type { MemberRow } from "./liveCircle";
import { trailPieces, trailPoints, trailRange, type TrailPiece } from "./trail";

/** A new fix refetches the trail once the last attempt is at least this old; newer fixes extend it meanwhile. */
const REFETCH_AFTER_MS = 30_000;

/**
 * The selected member's trail (`trailPieces`) from their recent history, as of
 * `nowMs`. None without a precise location: paused or approximate sharing
 * never shows one, and history fetched before such a spell is dropped, so it
 * cannot show again when a location reappears. An open SOS gets its own fetch
 * and a trail from its start only: while paused the alert reveals nothing
 * earlier, and the member may pause during it.
 */
export function useTrail(circleId: string, row: MemberRow | null, nowMs: number): TrailPiece[] {
  const queryClient = useQueryClient();
  const location = row?.location ?? null;
  const userId = row?.userId ?? "";
  const sosId = row?.sos?.id ?? null;
  // Pausing during the alert hides the history before it, which a cached copy would still show.
  const sosSince = row?.sos ? Date.parse(row.sos.since) : undefined;
  const trail = useQuery({
    queryKey: ["circle", circleId, "trail", userId, sosId],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/circles/{circleId}/members/{userId}/history", {
          params: { path: { circleId, userId }, query: trailRange(Date.now()) },
          signal,
        }),
      ),
    enabled: location !== null,
    staleTime: REFETCH_AFTER_MS,
    gcTime: 0,
    retry: 1,
  });

  const hidden = userId !== "" && location === null;
  useEffect(() => {
    if (hidden) queryClient.removeQueries({ queryKey: ["circle", circleId, "trail", userId] });
  }, [hidden, queryClient, circleId, userId]);

  const { refetch, dataUpdatedAt, errorUpdatedAt, isFetching } = trail;
  // Failures count too, so a failing endpoint is not asked again on every fix.
  const attemptedAt = Math.max(dataUpdatedAt, errorUpdatedAt);
  const fixTs = location?.ts;
  useEffect(() => {
    if (fixTs === undefined || isFetching || attemptedAt === 0) return;
    if (Date.now() - attemptedAt >= REFETCH_AFTER_MS) void refetch();
    // Only a new fix asks for a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixTs]);

  return useMemo(() => {
    if (!location || !trail.data || trail.data.user_id !== userId) return [];
    return trailPieces(trailPoints(trail.data, location, nowMs, sosSince), location.lon);
  }, [location, trail.data, userId, nowMs, sosSince]);
}
